/*
 * ********************************************************************
 *  GlobePayroll Confkeyential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbkeyden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {Injectable} from '@angular/core';

import {Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';

import * as moment from 'moment';

import {
  EmployeeInfo,
  EntityType,
  OrgEntity,
  OrgRequestParams,
  RepEntityUserManagement,
  TreeRootResponse
} from './org-interfaces';

import {REQUEST_OPTIONS} from '../app.constants';
import {AnyData} from '../shared/api';
import {ApiService} from '../shared/api/api.service';
import {SimplifiedPaySerieDTO, EntityDTO} from '../shared/api/GaApi';
import {
  GroupZoneDescriptor,
  TabSheetDescriptor,
  ZoneDescriptor
} from '../shared/form-generator/form-generator.interfaces';
import {FormGeneratorUtils} from '../shared/form-generator/form-generator.utils';
import {TreeNode} from '../shared/ui';

interface EmpContract {
  id: number;
  cliEmp: {
    id: number;
    firstName: string;
    lastName: string;
    photoUuid: string;
  };
}

export const groupByEmployee = (data: EmpContract[]) => {

  const groupByEmployeeInfo = data.reduce((acc: { [key: number]: EmployeeInfo }, value) => {
    if (!acc[value.cliEmp.id]?.contracts) {
      acc[value.cliEmp.id] = {
        id: value.cliEmp.id,
        firstName: value.cliEmp.firstName,
        lastName: value.cliEmp.lastName,
        contracts: [],
        photoLink: {
          rel: 'photo',
          href: value.cliEmp.photoUuid ? `/ga/api/v1/cliEmp/${value.cliEmp.id}/picture/${value.cliEmp.photoUuid}` : null,
          type: 'GET'
        }
      };
    }
    acc[value.cliEmp.id].contracts.push(value.id);

    return acc;
  }, {});
  return Object.values(groupByEmployeeInfo);
};

export const responseCallbackToTreeNodes = (response: any, lookupMap: Map<any, any>, isDefaultRecipient?: boolean): TreeNode<any>[] => {
  const list: any = response;
  const tree = [];

  list.forEach((item: AnyData) => {
    return lookupMap.set(item.id, {
      text: item.name,
      value: item.id < 0 ? -item.id : item.id,
      data: item,
      selectable: isDefaultRecipient
        ? item.userSelectable !== false
        &&
        item.detailedInhabitants?.filter(inhabitant =>
          new Date(inhabitant.beginDate) <= new Date() && new Date() <= new Date(inhabitant.endDate)).length
        : item.userSelectable !== false,
      visible: true,
      children: []
    });
  });

  for (const item of Array.from(lookupMap.values())) {
    const parent: TreeNode<AnyData> = lookupMap.get(item.data.parentId);
    if (parent !== undefined) {
      parent.children.push(item);
    } else {
      tree.push(item);
    }
  }
  return tree;
};

@Injectable()
export class OrgTreeService {
  public enityTypes: any = [];

  private descriptors: any = {};

  constructor(private api: ApiService) {
  }

  public getEntitiesByFtpSearch(ftpSearch: string) {
    return this.api.get(`/ga/api/v1/entity?ftsSearch=${ftpSearch}`);
  }

  public getTreeRoots(type: string, includePositions = false): Observable<TreeRootResponse[]> {
    const params = includePositions ? `type=${type}&includePositions=true` : `type=${type}`;
    return this.api.get(`/repository/api/v1/global/entityTree?${params}`);
  }

  public getTreeOrgs(params: OrgRequestParams): Observable<OrgEntity[]> {
    return this.api.get(`/ga/api/v1/entity/getChain`, {
      requestOptions: {
        params
      }
    })
      .pipe(
        map((res: { data: OrgEntity[] }) => res.data.map(item => Object.assign(item, item.attributes))),
        tap((res: OrgEntity[]) => {
          const ids: Set<number> = new Set<number>();

          res.forEach((entity: OrgEntity) => ids.add(entity.id));
          res.forEach((entity: OrgEntity) => {
            if (!ids.has(entity.parent)) {
              entity.parent = undefined;
            }
          });
        })
      );
  }

  public getEntityChildren(repEntityId: number, effectiveDate: string) {
    const search: string = `?effectiveDate=${effectiveDate}&repEntityId=${repEntityId}`;

    return this.api.get(`/ga/api/v1/entity/getChildren${search}`)
      .pipe(map(res => res.data.map(item => Object.assign(item, item.attributes))));
  }

  public getFormDescriptor(treeName: string, entityType: string = 'Company', orgId?: number) {
    let url = `/repository/api/v1/global/entityFormDescriptor/${treeName}/${entityType}`;

    if (orgId) {
      url += `/${orgId}`;
    }

    this.descriptors = [];
    if (!this.descriptors[treeName]) {
      this.descriptors[treeName] = {};
    }

    if (!this.descriptors[treeName][entityType]) {
      this.descriptors[treeName][entityType] = {};

      return this.api.get(url)
        .pipe(map(result => {
          result.forEach(item => {
            if (item.formDescriptor) {
              item.formDescriptor.tabSheets = item.formDescriptor.tabSheets
                .filter((tabSheet: TabSheetDescriptor) => tabSheet.groupZones.length)
                .map((tabSheet: TabSheetDescriptor) => {
                  tabSheet.groupZones
                    .forEach((groupZone: GroupZoneDescriptor) => {
                      groupZone.zones.forEach((zone: ZoneDescriptor) => {
                        if (zone.type === 'FILE') {
                          if (!zone.metadatas) {
                            zone.metadatas = {};
                          }
                        }
                      });
                    });
                  return tabSheet;
                });

              if (FormGeneratorUtils.isFileZoneExist(item.formDescriptor)) {
                FormGeneratorUtils.AddAttachemtsTabEmpty(item.formDescriptor.tabSheets);
              }
            }
          });
          this.descriptors[treeName][entityType] = result;

          return result;
        }))
        .toPromise();
    }

    return Promise.resolve(this.descriptors[treeName][entityType]);
  }

  public getOrgData(orgId: number) {
    return this.api.ga20.get(`/entity/${orgId}`);
  }

  public getCliRegData(): Observable<any> {
    return this.api.get('/repository/api/v1/cliReg');
  }

  public getTreeEntityTypes(treeName: string): Observable<EntityType[]> {
    return this.api.get(`/repository/api/v1/global/entityTree/${treeName}/type?orderBy=entityLevel,name`);
  }

  public updateOrgData(orgId: number, cliRegId: number, data: any) {
    return this.api.post(`/ga/api/v1/entity/${orgId}/data?cliRegId=${cliRegId}`, data);
  }

  public addOrgEntity(data: any) {
    return this.api.post(`/ga/api/v1/entity`, data);
  }

  public copyOrgEntity(data: any) {
    return this.api.post(`/ga/api/v1/entity/copyFrom`, data);
  }

  public updateOrgEntity(orgId: number, data: any, effectiveDate = moment().format('YYYY-MM-DD')) {
    const url = `/ga/api/v1/entity/${orgId}?effectiveDate=${effectiveDate}`;
    return this.api.post(url, data);
  }

  public availableLevels(entityTypes, entityLevel) {
    if (!entityTypes) {
      return [];
    }

    return entityTypes.filter(entityType => {
      return !entityType.level || entityType.level === entityLevel + 1 || entityType.level === -1;
    });
  }

  public deleteOrg(id: number) {
    return this.api.delete(`/ga/api/v1/entity/${id}`);
  }

  public getEmployeesInfos(id: number): Observable<EmployeeInfo[]> {
    return this.api.get(`/ga/api/v2/entity/${id}/contracts?include-children=false`).pipe(
      map(data => groupByEmployee(data))
    );
  }

  public getReferencingEntities(id: number): Observable<EntityDTO[]> {
    return this.api.get(`/ga/api/v2/entity/${id}/referencing-entities`);
  }

  public getConfidentialityFields(userIds: string, tree: string, type: string): Observable<any> {
    return this.api.get(`/ga/api/v1/entity/getConfidentialityFields`, {
      requestOptions: {
        params: {userIds, tree, type}
      }
    });
  }

  public getEntitiesByName(name: string, date: string): Observable<any> {
    return this.api.get(`/ga/api/v1/entity/filter?effectiveDate=${date}&name=${encodeURIComponent(name)}`);
  }

  public getParentsTree(tree: string) {
    return this.api.get('/ga/api/v1/entity/getRepEntitiesShortViewByTree', {
      requestOptions: {
        params: {tree}
      }
    });
  }

  public openDebugFlatten(entityId: number) {
    if (!entityId) {
      return;
    }

    this.api.ga20.get(`/entity/${entityId}/debug-flatten?begindate=1970-01-01&enddate=3000-01-01`,
      {requestOptions: REQUEST_OPTIONS.RESPONSE_TYPE.TEXT.requestOptions})
      .subscribe(body => {
        const file = new Blob([body], {type: 'text/plain'});
        const objectUrl = URL.createObjectURL(file);
        window.open(objectUrl);
      });
  }

  public getUserManagement(entityId: number): Observable<RepEntityUserManagement> {
    return this.api.ga.get(`/entity/${entityId}/user-management`);
  }

  public createOrUpdateUserManagement(entityId: number, userManagement: RepEntityUserManagement): Observable<RepEntityUserManagement> {
    return this.api.ga.put(`/entity/${entityId}/user-management`, userManagement);
  }

  public deleteUserManagement(entityId: number): Observable<RepEntityUserManagement> {
    return this.api.ga.delete(`/entity/${entityId}/user-management`);
  }

  public getPaySeries(): Observable<SimplifiedPaySerieDTO[]> {
    return this.api.ga20.get('/entity/pay-serie');
  }

  public isRepEntityOtherIdTaken(otherId: string): Observable<boolean> {
    return this.api.post(`/ga/api/v1/entity/checkRepEntity?repEntityOtherId=${otherId}`);
  }

}
